/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React from 'react';
import { Button } from 'reactstrap';
import Loader from '../loader'
import { SelectOption } from '../../types/select';
import G17Client from '../../services/G17Client';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { FilterOption} from '../settings-sidebar';
import { getSDGOptions } from '../survey/survey-question-list-toolbar/partials/FilterQuestions';
import { InitiativePlain, MaterialityMap } from '../../types/initiative';
import './style.scss';

interface SDGSelectorProps {
  initiative: InitiativePlain;
  toggle: () => void;
  handleReload?: () => void;
  callbackSubmit?: () => void;
}

const SDGSelector = (props: SDGSelectorProps) => {
  const { handleReload, initiative, toggle, callbackSubmit = () => {} } = props;

  const [filterByGoalHigh, setSDGGoalHigh] = React.useState<string[]>(initiative?.materialityMap?.high ?? []);
  const [filterByGoalMedium, setSDGGoalMedium] = React.useState<string[]>(initiative?.materialityMap?.medium ?? []);
  const [filterByGoalLow, setSDGGoalLow] = React.useState<string[]>(initiative?.materialityMap?.low ?? []);

  const sdgOptions = getSDGOptions();
  const sdgGoals = sdgOptions.filter(option => option.value !== null) as SelectOption<string>[];
  const options = sdgGoals.filter(option => !filterByGoalHigh.includes(option.value) && !filterByGoalMedium.includes(option.value) && !filterByGoalLow.includes(option.value));

  const [isSaving, setSaving] = React.useState(false);
  const [errorMessage, setMessage] = React.useState('');

  const onUpdate = () => {
    handleUpdate({
      high: filterByGoalHigh,
      medium: filterByGoalMedium,
      low: filterByGoalLow,
      none: options.map((o) => o.value),
    });
    callbackSubmit();
  }

  const handleUpdate = (materialityMap: MaterialityMap | null) => {
    setSaving(true);

    return G17Client.patch(`initiatives/${initiative._id}/materiality`, materialityMap)
      .then(async () => {
        setSaving(false);
        const analytics = getAnalytics();
        await analytics.track(AnalyticsEvents.MaterialityCustomized, {
          initiativeId: initiative._id
        });
        if (handleReload) {
          await handleReload();
        }
        toggle();
      })
      .catch((e: Error) => setMessage(e.message));
  }

  return <>
    {isSaving && <Loader />}
    {errorMessage && <div className='alert alert-primary'>{errorMessage}</div>}
    <p>Select the SDGs that are most important to you.</p>
    <div className='h5 mt-4'>
      <i className='fa fa-tachometer-alt text-danger mr-2'/>
      High
    </div>
    <FilterOption
      closeMenuOnSelect={false}
      isClearable={true}
      isMulti
      placeholder='Select SDGs'
      options={options}
      onChange={(selectedOptions) => setSDGGoalHigh(selectedOptions ? selectedOptions.map(o => o.value) : [])}
      value={sdgGoals.filter(option => filterByGoalHigh.includes(option.value as string))}
    />
    <div className='h5 mt-4'>
      <i className='fa fa-tachometer-alt text-warning mr-2'/>
      Medium
    </div>
    <FilterOption
      closeMenuOnSelect={false}
      isMulti
      placeholder='Select SDGs'
      isClearable={true}
      options={options}
      onChange={(selectedOptions) => setSDGGoalMedium(selectedOptions ? selectedOptions.map(o => o.value) : [])}
      value={sdgGoals.filter(option => filterByGoalMedium.includes(option.value as string))}
    />
    <div className='h5 mt-4'>
      <i className='fa fa-tachometer-alt text-success mr-2'/>
      Low
    </div>
    <FilterOption
      closeMenuOnSelect={false}
      isMulti
      placeholder='Select SDGs'
      isClearable={true}
      options={options}
      onChange={(selectedOptions) => setSDGGoalLow(selectedOptions ? selectedOptions.map(o => o.value) : [])}
      value={sdgGoals.filter(option => filterByGoalLow.includes(option.value as string))}
    />
    <div className='text-right mt-5 d-flex'>
      <Button color='link-secondary' onClick={() => handleUpdate(null)}>Use industry default</Button>
      <Button color='link-secondary' onClick={toggle} className='ms-auto me-3'>Cancel</Button>
      <Button color='primary' disabled={errorMessage !== ''} onClick={() => onUpdate()}>Save</Button>
    </div>
  </>

}


export default SDGSelector;
